import { Component } from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { TranslationsModule } from '../../../../../../translations';

export interface OILinkNotificationTableValues {
    notifyAssessmentSender: boolean;
    notifyAssessmentTaker: boolean;
    notifyStakeholders: boolean;
    includeBAReportInLinkSenderEmail: boolean;
    includeBAReportInAssessmentTakerEmail: boolean;
    includeBAReportInStakeholderEmail: boolean;
}

type NotificationTableOptionValue =
    | 'none'
    | 'completionEmail'
    | 'emailAndBAReport';

@Component({
    selector: 'oi-notifications-table',
    imports: [MatRadioModule, FormsModule, TranslationsModule],
    templateUrl: './oi-notifications-table.component.html',
    styleUrls: ['./oi-notifications-table.component.less'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: OiNotificationsTableComponent,
            multi: true
        }
    ]
})
export class OiNotificationsTableComponent implements ControlValueAccessor {
    notifyLinkOwner: NotificationTableOptionValue;
    notifyStakeholders: NotificationTableOptionValue;
    notifyAssessmentTaker: NotificationTableOptionValue;

    options: NotificationTableOptionValue[] = [
        'none',
        'completionEmail',
        'emailAndBAReport'
    ];

    touched = false;

    onLinkOwnerChange(value: NotificationTableOptionValue) {
        this.notifyLinkOwner = value;
        this.processChange();
    }
    onStakeholderChange(value: NotificationTableOptionValue) {
        this.notifyStakeholders = value;
        this.processChange();
    }
    onAssessmentTakerChange(value: NotificationTableOptionValue) {
        this.notifyAssessmentTaker = value;
        this.processChange();
    }

    processChange() {
        this.markAsTouched();
        this.onChange({
            notifyAssessmentSender: this.getCompletionEmailValue(
                this.notifyLinkOwner
            ),
            notifyAssessmentTaker: this.getCompletionEmailValue(
                this.notifyAssessmentTaker
            ),
            notifyStakeholders: this.getCompletionEmailValue(
                this.notifyStakeholders
            ),
            includeBAReportInLinkSenderEmail: this.getIncludeBAValue(
                this.notifyLinkOwner
            ),
            includeBAReportInAssessmentTakerEmail: this.getIncludeBAValue(
                this.notifyAssessmentTaker
            ),
            includeBAReportInStakeholderEmail: this.getIncludeBAValue(
                this.notifyStakeholders
            )
        });
    }

    registerOnChange(
        onChange: (values: OILinkNotificationTableValues) => void
    ): void {
        this.onChange = onChange;
    }

    onChange = (value: OILinkNotificationTableValues) => {};

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    onTouched = () => {};

    writeValue(values?: OILinkNotificationTableValues): void {
        this.notifyLinkOwner = this.getNotificationValue(
            values?.notifyAssessmentSender,
            values?.includeBAReportInLinkSenderEmail
        );
        this.notifyStakeholders = this.getNotificationValue(
            values?.notifyStakeholders,
            values?.includeBAReportInStakeholderEmail
        );
        this.notifyAssessmentTaker = this.getNotificationValue(
            values?.notifyAssessmentTaker,
            values?.includeBAReportInAssessmentTakerEmail
        );
    }

    private markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private getNotificationValue(
        notify: boolean,
        includeBA: boolean
    ): NotificationTableOptionValue {
        if (notify && !includeBA) {
            return 'completionEmail';
        } else if (notify && includeBA) {
            return 'emailAndBAReport';
        } else {
            return 'none';
        }
    }

    private getCompletionEmailValue(field: NotificationTableOptionValue) {
        return field === 'completionEmail' || field === 'emailAndBAReport';
    }

    private getIncludeBAValue(field: NotificationTableOptionValue) {
        return field === 'emailAndBAReport';
    }
}
